import apiClient from "./client";

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface MfaVerificationRequest {
  email: string;
  token?: string;
  backupCode?: string;
}

export interface User {
  id: string;
  userId?: string; // MongoDB _id as string, matches JWT payload
  email: string;
  name: string;
  fullName?: string;
  firstName?: string;
  lastName?: string;
  role: string;
  tenantId?: string;
  tenantName?: string;
  tenantLogo?: string;
  permissionCodes?: string[];
  isSuperAdmin?: boolean;
}

export interface AuthResponse {
  token: string;
  refreshToken?: string;
  user: User;
  mfaRequired?: boolean;
  message?: string;
  email?: string;
}

export interface MfaSetupResponse {
  success: boolean;
  data: {
    qrCodeUrl: string;
    manualEntryKey: string;
    backupCodes: string[];
  };
}

export interface MfaStatusResponse {
  success: boolean;
  mfaEnabled: boolean;
  backupCodesCount: number;
}

export const authApi = {
  login: async (credentials: LoginCredentials): Promise<AuthResponse> => {
    try {
      const response = await apiClient.post<AuthResponse>(
        "/auth/login",
        credentials
      );
      return response.data;
    } catch (error: any) {
      // Extract error message from API response and re-throw with proper message
      const errorMessage =
        error.response?.data?.error ||
        error.response?.data?.message ||
        "Invalid credentials";
      const enhancedError = new Error(errorMessage);
      // Preserve original error properties for the frontend to access
      (enhancedError as any).response = error.response;
      throw enhancedError;
    }
  },

  verifyMfa: async (data: MfaVerificationRequest): Promise<AuthResponse> => {
    const response = await apiClient.post<AuthResponse>(
      "/auth/mfa/verify",
      data
    );
    return response.data;
  },

  setupMfa: async (): Promise<MfaSetupResponse> => {
    const response = await apiClient.post<MfaSetupResponse>("/auth/mfa/setup");
    return response.data;
  },

  enableMfa: async (
    token: string
  ): Promise<{ success: boolean; message: string }> => {
    const response = await apiClient.post("/auth/mfa/enable", { token });
    return response.data;
  },

  disableMfa: async (
    password: string
  ): Promise<{ success: boolean; message: string }> => {
    const response = await apiClient.post("/auth/mfa/disable", { password });
    return response.data;
  },

  regenerateBackupCodes: async (
    password: string
  ): Promise<{ success: boolean; backupCodes: string[] }> => {
    const response = await apiClient.post("/auth/mfa/backup-codes", {
      password,
    });
    return response.data;
  },

  getMfaStatus: async (): Promise<MfaStatusResponse> => {
    const response = await apiClient.get<MfaStatusResponse>("/auth/mfa/status");
    return response.data;
  },

  logout: async (): Promise<void> => {
    await apiClient.post("/auth/logout");
  },

  getCurrentUser: async (): Promise<User> => {
    const response = await apiClient.get<{ success: boolean; user: User }>(
      "/auth/me"
    );
    return response.data.user;
  },

  refreshToken: async (refreshToken: string): Promise<AuthResponse> => {
    const response = await apiClient.post<{
      success: boolean;
      token: string;
      refreshToken: string;
      user: User;
    }>("/auth/refresh", { refreshToken });
    return {
      token: response.data.token,
      refreshToken: response.data.refreshToken,
      user: response.data.user,
    };
  },
};

/**
 * Helper function to check if user has a specific permission
 */
export function hasPermission(
  user: User | null,
  permissionCode: string
): boolean {
  if (!user) return false;
  if (user.isSuperAdmin) return true;
  return user.permissionCodes?.includes(permissionCode) ?? false;
}

/**
 * Helper function to check if user has ANY of the specified permissions
 */
export function hasAnyPermission(
  user: User | null,
  permissionCodes: string[]
): boolean {
  if (!user) return false;
  if (user.isSuperAdmin) return true;
  return (
    permissionCodes.some((code) => user.permissionCodes?.includes(code)) ??
    false
  );
}

/**
 * Helper function to check if user has ALL of the specified permissions
 */
export function hasAllPermissions(
  user: User | null,
  permissionCodes: string[]
): boolean {
  if (!user) return false;
  if (user.isSuperAdmin) return true;
  return (
    permissionCodes.every((code) => user.permissionCodes?.includes(code)) ??
    false
  );
}

/**
 * Helper function to check if user is superadmin or tenant admin
 * AI Automation should only be available to these high-level roles
 */
export function isAdminLevel(user: User | null): boolean {
  if (!user) return false;
  
  // Super admin always has access
  if (user.isSuperAdmin) return true;
  
  // Check for tenant admin permissions - they typically have TENANTS_UPDATE permission
  return user.permissionCodes?.includes('TENANTS_UPDATE') ?? false;
}
